// Centralized API calls for the booking flow (Google-only, Approach A)
import { http } from "@services/http";

// Autocomplete cities (server proxies to Google Places Autocomplete NEW)
export async function autocompleteCities({ input, sessionToken, country, signal }) {
  if (!input || input.length < 2) return [];
  const { data } = await http.get('/api/geo/autocomplete', {
    params: { q: input, session: sessionToken, country },
    signal, // AbortController (Axios v1+ supports this)
  });
  return data?.results || [];
}

// Reverse geocode — turn coordinates into city/state/label
export async function reverseGeocode({ lat, lon, signal }) {
  const { data } = await http.get('/api/geo/reverse', {
    params: { lat, lon },
    signal,
  });
  // expected: { label, city, state, country, lat, lon }
  return data;
}

// Place Details (NEW) — get lat/lon for the selected placeId
export async function getPlaceLocation({ placeId, sessionToken, signal }) {
  const { data } = await http.get('/api/geo/details', {
    params: { placeId, session: sessionToken },
    signal,
  });
  // expected: { lat, lon }
  return data;
}

// Hotels search (POST) with pagination and filters
export async function searchHotels(payload) {
  // payload: { where, checkIn, checkOut, guests, filters, sortBy, page, pageSize }
  const { data } = await http.post('/api/hotels/search', payload);
  // expected: { results, page, pageSize, total, totalPages, hasMore }
  console.log('searchHotels response data:', data);
  return data;
}

export async function getHotelDetails(id, params = {}) {
  const { data } = await http.get(`/api/hotels/${id}`, { params });
  return data;
}

export async function createReservationHold(payload) {
  // payload: vendor_id, checkin, checkout, guests{adults,children,rooms}, childAges[], roomId, rateId
  // Returns: { status, data: cartArray, liveCheck: derbyResponseData }
  const { data } = await http.post('/api/reservation-hold', payload);
  return data;
}

export async function getReservationHold(cartId) {
  const { data } = await http.get(`/api/reservation-hold/${cartId}`);
  return data;
}

export async function confirmReservationHold(cartId, payload) {
  const { data } = await http.post(`/api/reservation-hold/${cartId}/confirm`, payload);
  return data;
}

export async function searchCategory(category, body) {
  const { data } = await http.post('/search', { category, ...body });
  return data;
}

export async function prebookRoom(payload) {
  const cartId =
    typeof payload === "string"
      ? payload
      : payload?.cartId ?? payload?.cart_id ?? null;
  if (!cartId) {
    throw new Error("cartId is required for prebook");
  }
  const { data } = await http.post('/api/vendors/derbysoft-prebook', {
    cartId,
  });
  return data;
}

export async function bookRoom(payload) {
  const cartId =
    typeof payload === "string"
      ? payload
      : payload?.cartId ?? payload?.cart_id ?? null;
  if (!cartId) {
    throw new Error("cartId is required for booking");
  }
  // justification (optional) is forwarded to the backend so the policy
  // engine can short-circuit a `require_justification` policy.
  const justification =
    typeof payload === "object" && payload !== null
      ? payload?.justification
      : undefined;
  const body = { cartId };
  if (justification && String(justification).trim() !== '') {
    body.justification = String(justification).trim();
  }
  const { data } = await http.post('/api/vendors/derbysoft-book', body);
  return data;
}

export async function getDerbyAvailability(vendorId, payload) {
  const { data } = await http.post(`/api/reservation/vendors/${vendorId}/availability`, payload);
  console.log('getDerbyAvailability response data:', data);
  return data;
}

export async function searchHotelsWithAvailability(payload, options = {}) {
  const { data } = await http.post(
    '/api/reservation/search-with-availability',
    payload,
    options,
  );
  return data;
}

export async function getReservation(confirmationId) {
  const { data } = await http.get(`/api/reservations/${confirmationId}`);
  return data;
}

export async function modifyReservation(confirmationId, payload) {
  const { data } = await http.post(`/api/reservations/${confirmationId}/modify`, payload);
  return data;
}

export async function queryReservation(payload) {
  // payload should include at minimum: vendorId, distributorResId
  const body = {
    vendor_id: payload.vendorId ?? payload.vendor_id,
    distributorResId: payload.distributorResId ?? payload.distributor_res_id,
  };
  if (!body.vendor_id || !body.distributorResId) {
    throw new Error("vendor_id and distributorResId are required to query reservation");
  }
  if (payload.derbyResId) body.derbyResId = payload.derbyResId;
  if (payload.supplierResId) body.supplierResId = payload.supplierResId;
  if (payload.extensions) body.extensions = payload.extensions;

  const { data } = await http.post('/api/vendors/derbysoft-query-reservation', body);
  return data; // expected: { status, message, data: { header, reservations: [...] } }
}

export async function cancelReservation(confirmationId, payload = {}) {
  const { data } = await http.post(`/api/reservations/${confirmationId}/cancel`, payload);
  return data;
}

export async function getCancellationPreview(confirmationId) {
  const { data } = await http.get(
    `/api/reservations/${encodeURIComponent(confirmationId)}/cancellation-preview`,
  );
  return data; // { fee, currency, penalty_type, free_until, is_free }
}

export async function listReservations(params = {}) {
  const { bucket = "upcoming", page = 1, perPage = 10, sort, q } = params;
  const { data } = await http.get(`/api/reservations`, {
    params: {
      bucket,
      page,
      perPage,
      ...(sort ? { sort } : {}),
      // Omitted when blank so the cache key for "no search" is identical
      // whether the box was never touched or was typed in and cleared.
      ...(q ? { q } : {}),
    },
  });
  return data;
}

export async function getNextReservation() {
  const { data } = await http.get(`/api/reservations/next`);
  return data;
}
